import React from 'react';
import {
  Typography, Button, Box, TextField, Alert, CircularProgress,
  Tabs, Tab, FormControlLabel, Checkbox, IconButton,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PriceScanner from '../../components/PriceScanner';
import { apiFetch } from '../../apiFetch';
import { haptics } from '../../utils/haptics';
import { emptyGroceryFormData } from '../../types/Grocery';
import { EnterPriceStepProps } from './types';
import TabPanel from './TabPanel';
import { analytics } from '../../services/analytics';

const EnterPriceStep: React.FC<EnterPriceStepProps> = ({
  grocery,
  setGrocery,
  error,
  setError,
  loading,
  setLoading,
  onClose,
  isMobile,
  apiUrl,
  shopId,
  shopName,
  priceTabValue,
  setPriceTabValue,
  price,
  setPrice,
  isDiscounted,
  setIsDiscounted,
  priceBeforeDiscount,
  setPriceBeforeDiscount,
  scannedPrice,
  setScannedPrice,
  priceScannerActive,
  priceScannerResetTrigger,
  setPriceScannerResetTrigger,
  wasNewGrocery,
  wasAddedToList,
  existingGroceries,
  prices,
  setActiveStep,
  setSelectedCatalog,
  setFormData,
  onSuccess,
}) => {
  const handlePriceTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setPriceTabValue(newValue);
    setError(null);
  };

  const handleDiscountChange = (checked: boolean) => {
    setIsDiscounted(checked);
    if (checked && !priceBeforeDiscount && grocery) {
      const existingPrice = prices.get(grocery.id);
      if (existingPrice) {
        if (!existingPrice.is_discounted) {
          setPriceBeforeDiscount(existingPrice.price);
        } else if (existingPrice.price_before_discount) {
          setPriceBeforeDiscount(existingPrice.price_before_discount);
        }
      }
    }
  };

  const handlePriceDetected = (detectedPrice: string) => {
    setScannedPrice(detectedPrice);
    setPrice(detectedPrice);
    setPriceTabValue(1);
  };

  const handleScanPriceAgain = () => {
    setScannedPrice(null);
    setPriceScannerResetTrigger((prev) => prev + 1);
    setPriceTabValue(0);
  };

  const handleSkipPrice = () => {
    if (!grocery) return;

    haptics.medium();

    onSuccess({
      grocery,
      price: 0,
      wasNewGrocery,
      wasAddedToList,
    });

    onClose();
  };

  const validatePrice = (): boolean => {
    if (!price || isNaN(parseFloat(price)) || parseFloat(price) < 0) {
      setError('Please enter a valid price.');
      return false;
    }
    if (isDiscounted && priceBeforeDiscount && isNaN(parseFloat(priceBeforeDiscount))) {
      setError('Please enter a valid original price before discount.');
      return false;
    }
    // Original price must be higher than sale price
    if (isDiscounted && priceBeforeDiscount && parseFloat(priceBeforeDiscount) <= parseFloat(price)) {
      setError('Original price must be higher than the sale price.');
      return false;
    }
    return true;
  };

  const handleSavePrice = async () => {
    if (!grocery || !validatePrice()) return;

    setLoading(true);
    setError(null);

    try {
      const payload = {
        shop: shopId,
        grocery: grocery.id,
        price: parseFloat(price).toFixed(2),
        is_discounted: isDiscounted,
        price_before_discount: isDiscounted && priceBeforeDiscount ? parseFloat(priceBeforeDiscount).toFixed(2) : null,
      };

      await apiFetch(`${apiUrl}/api/prices/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      analytics.trackEvent('price_submit', 'feature', {
        is_discounted: isDiscounted,
        has_scanned_price: !!scannedPrice,
      });
      haptics.success();

      onSuccess({
        grocery,
        price: parseFloat(price),
        wasNewGrocery,
        wasAddedToList,
      });

      onClose();
    } catch (err: any) {
      haptics.error();
      setError(err.message || 'Failed to save price');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (wasAddedToList) {
      setActiveStep(1);
    } else {
      setActiveStep(0);
      setGrocery(null);
      setSelectedCatalog(null);
      setFormData(emptyGroceryFormData);
    }
  };

  if (!grocery) return null;

  const isInList = existingGroceries.some((item) => item.grocery === grocery.id) || wasAddedToList;
  const existingPrice = prices.get(grocery.id);

  return (
    <>
      {/* Dynamic messaging based on shopping list and price status */}
      {isInList && existingPrice ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {grocery.name} is already in your shopping list
          </Typography>
          <Typography variant="body2">
            You can update the price if it's changed, otherwise press Cancel to continue shopping.
          </Typography>
        </Alert>
      ) : !isInList && existingPrice ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {grocery.name} has not been added to your list
          </Typography>
          <Typography variant="body2">
            If you like, you can update the price at {shopName} for your general grocery database for future use. Otherwise press Cancel to continue shopping.
          </Typography>
        </Alert>
      ) : (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Enter price for "{grocery.name}" at {shopName}
        </Typography>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={priceTabValue}
          onChange={handlePriceTabChange}
          variant="fullWidth"
        >
          <Tab icon={<CameraAltIcon />} iconPosition="start" label="Scan" />
          <Tab icon={<EditIcon />} iconPosition="start" label="Manual" />
        </Tabs>
      </Box>

      {/* Scan Price Tab */}
      <TabPanel value={priceTabValue} index={0}>
        <PriceScanner
          onPriceDetected={handlePriceDetected}
          onError={(err) => setError(err)}
          active={priceScannerActive}
          resetTrigger={priceScannerResetTrigger}
        />
        {scannedPrice && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Price ${scannedPrice} detected! Review in Manual tab.
          </Alert>
        )}
      </TabPanel>

      {/* Manual Price Tab */}
      <TabPanel value={priceTabValue} index={1}>
        {scannedPrice && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={handleScanPriceAgain}>
                Scan Again
              </Button>
            }
          >
            Scanned price: ${scannedPrice}
          </Alert>
        )}

        <TextField
          label="Price"
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          fullWidth
          inputProps={{ min: 0, step: '0.01' }}
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={isDiscounted}
              onChange={(e) => handleDiscountChange(e.target.checked)}
            />
          }
          label="This is a discounted price"
        />

        {isDiscounted && (
          <TextField
            label="Original Price (before discount)"
            type="number"
            value={priceBeforeDiscount}
            onChange={(e) => setPriceBeforeDiscount(e.target.value)}
            fullWidth
            placeholder="Optional"
            inputProps={{ min: 0, step: '0.01' }}
            sx={{ mt: 1 }}
            helperText="Leave blank if original price is unknown"
          />
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          {isMobile ? (
            <IconButton onClick={handleBack} disabled={loading}>
              <ArrowBackIcon />
            </IconButton>
          ) : (
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={loading}
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button variant="outlined" onClick={handleSkipPrice} disabled={loading}>
              Skip
            </Button>
            <Button
              variant="contained"
              onClick={handleSavePrice}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Save Price'}
            </Button>
          </Box>
        </Box>
      </TabPanel>
    </>
  );
};

export default EnterPriceStep;
